import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

export const fetchCompanyDetails = createAsyncThunk(
  'companyDetails/fetchCompanyDetails',
  async ({ limit = 50, forceRefresh = false, query = '', batchSize = 10, filters = {} }, { getState, rejectWithValue }) => {
    try {
      const { cachedData } = getState().companyDetails;

      const cacheKey = `companies_${query || 'default'}`;
      const currentCache = cachedData?.[cacheKey];
      const cacheValid = currentCache?.timestamp && (Date.now() - currentCache.timestamp < 3600 * 1000);

      if (!forceRefresh && cacheValid && currentCache.data) {
        return { data: currentCache.data, fromCache: true };
      }

      const res = await axios.post(
        "http://52.66.244.57/consumer_api/get_company_details?sectorname=All&companyname=All&categoryname=All",
        {},
        { headers: { Accept: "application/json" } }
      );

      const companies = res.data || [];
      let companiesToFetch;
      if (limit === 0) {
        companiesToFetch = companies.slice(0, 50);
      } else {
        companiesToFetch = companies.slice(0, limit);
      }


      const results = [];
      const start_date = filters.startDate
        ? new Date(filters.startDate).toISOString().split('T')[0]
        : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0]

      const end_date = filters.endDate
        ? new Date(filters.endDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]

      for (let i = 0; i < companiesToFetch.length; i += batchSize) {
        const batch = companiesToFetch.slice(i, i + batchSize);

        const batchResults = await Promise.all(
          batch.map(async (company, batchIndex) => {
            const globalIndex = i + batchIndex;
            const payload = {
              query: query,
              skip: 0,
              size: 0,
              start_date: start_date,
              end_date: end_date,
              value: 2,
              CityName: "All",
              stateName: "All",
              complaintType: "All",
              complaintMode: "All",
              companyName: company.companyname,
              complaintStatus: "All",
              threshold: filters.threshold || 1.5,
              complaint_numbers: ["NA"],
            };

            try {
              const response = await axios.post(
                "http://52.66.244.57/consumer_api/search",
                payload,
                {
                  headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                  },
                  timeout: 15000, // Reduced to 15 seconds per request
                }
              );

              return {
                companyName: company.companyname,
                sectorName: company.sectorname,
                catName: company.catname,
                counts: response.data?.total_count || 0,
              };
            } catch (err) {
              const errorType = err.code === 'ECONNABORTED' ? 'timeout' : 'network';
              console.error(`Error fetching count for ${company.companyname} (${errorType}):`, err.message);
              return {
                companyName: company.companyname,
                sectorName: company.sectorname,
                catName: company.catname,
                counts: 0,
                error: errorType, // Track error type for debugging
              };
            }
          })
        );

        // Add batch results to main results array
        results.push(...batchResults);

        // Add small delay between batches to prevent overwhelming the server
        if (i + batchSize < companiesToFetch.length) {
          await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay between batches
        }
      }

      return { data: results, fromCache: false, cacheKey, query };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const initialState = {
  companyData: [],
  loading: false,
  error: null,
  cachedData: {}, // Changed to object to store multiple query-specific caches
};

const companyDetailsSlice = createSlice({
  name: 'companyDetails',
  initialState,
  reducers: {
    clearCompanyData: (state) => {
      state.companyData = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyDetails.fulfilled, (state, action) => {
        const { data, fromCache, cacheKey } = action.payload;
        state.companyData = data;

        if (!fromCache && cacheKey) {
          // Initialize cachedData as object if it doesn't exist
          if (!state.cachedData) {
            state.cachedData = {};
          }
          // Store data with query-specific cache key
          state.cachedData[cacheKey] = { data, timestamp: Date.now() };
        }

        state.loading = false;
      })
      .addCase(fetchCompanyDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch company details';
      });
  },
});

export const { clearCompanyData } = companyDetailsSlice.actions;
export default companyDetailsSlice.reducer;
